import { useTranslation } from "react-i18next";
import { useMemo } from "react";

import { usePageTitle } from "@/utils/usePageTitle";
import ToDoIcon from "@/assets/images/to-do/to-do.png";
import InProgressIcon from "@/assets/images/to-do/in-progress.png";
import DoneIcon from "@/assets/images/to-do/done.png";

import { ToDoStatus } from "../ToDo/ToDoList/enum/ToDoStatus.enum";
import { SubTaskInfo, TaskInfo } from "../TasksAndSubTasks/schema/tasks-and-subtasks";
import SwimlaneRow from "./components/SwimlaneRow";
import { useSelectedParam } from "../TasksAndSubTasks/useSelectedParam";

interface TagGroup {
	tagId: number | null;
	tagName: string;
	items: (TaskInfo | SubTaskInfo)[];
}
interface ToDoSwimlaneListProps {
	activeTaskId: number | null;
	selectedSubTaskId: number | null;
	isCreateTaskModalOpen: boolean;
	setIsCreateTaskModalOpen: (open: boolean) => void;
	tasks: TaskInfo[];
	subtasks: SubTaskInfo[];
	allItems: (TaskInfo | SubTaskInfo)[];

	setActiveTaskId: (id: number) => void;
	setSelectedSubTaskId: (id: number) => void;
	isLoading: boolean;
}

export default function ToDoSwimlaneList({ allItems, tasks, subtasks }: ToDoSwimlaneListProps) {
	const { t } = useTranslation();
	const ts = (key: string) => t(`to-do.${key}`);

	usePageTitle(ts("title"));

	const tagGroups = useMemo<TagGroup[]>(() => {
		const groupMap = new Map<number | null, TagGroup>();

		const parentTagMap = new Map<number, { tagId: number | null; tagName: string }>();

		allItems.forEach((item) => {
			if (item.isTask && item.id !== undefined) {
				parentTagMap.set(item.id, {
					tagId: item.tagId ?? null,
					tagName: item.tag?.name ?? ts("untagged"),
				});
			}
		});

		allItems.forEach((item) => {
			let tagId: number | null;
			let tagName: string;

			if (item.isTask) {
				tagId = item.tagId ?? null;
				tagName = item.tag?.name ?? ts("untagged");
			} else {
				const parentTaskId = (item as SubTaskInfo).taskId;
				const parentTag = parentTaskId !== undefined ? parentTagMap.get(parentTaskId) : undefined;

				tagId = parentTag?.tagId ?? null;
				tagName = parentTag?.tagName ?? ts("untagged");
			}

			if (!groupMap.has(tagId)) {
				groupMap.set(tagId, { tagId, tagName, items: [] });
			}

			groupMap.get(tagId)!.items.push(item);
		});

		const groups = Array.from(groupMap.values());

		// Sort: named tags first (alphabetically), untagged last
		groups.sort((a, b) => {
			if (a.tagId === null) {
				return 1;
			}

			if (b.tagId === null) {
				return -1;
			}

			return a.tagName.localeCompare(b.tagName);
		});

		return groups;
	}, [allItems, ts]);

	const { openSelected } = useSelectedParam();

	const handleShowTaskDetails = (taskId: number) => {
		const task = tasks.find((t) => t.id === taskId);

		openSelected(taskId, undefined, task?.tagId ?? 0);
	};

	const handleShowSubTaskDetails = (subTaskId: number) => {
		const sub = subtasks.find((s) => s.id === subTaskId);
		const parent = tasks.find((t) => t.id === sub?.taskId);

		openSelected(sub?.taskId ?? 0, subTaskId, parent?.tagId ?? 0);
	};

	return (
		<>
			<div className="gap-3 w-full">
				<div className="grid grid-cols-4 gap-3  w-full">
					<div className="flex gap-2 items-center justify-center px-2">
						<img alt="to-do" className="w-5 h-5" src={ToDoIcon} />
						<span className="text-[14px] font-medium text-primary-blue-dark">
							{ts(ToDoStatus.TO_DO)}
						</span>
					</div>
					<div className="flex gap-2 items-center px-2 justify-center">
						<img alt="in-progress" className="w-5 h-5" src={InProgressIcon} />
						<span className="text-[14px] font-medium text-primary-blue-dark">
							{ts(ToDoStatus.IN_PROGRESS)}
						</span>
					</div>
					<div className="flex gap-2 items-center px-2 justify-center">
						<img alt="in-progress" className="w-5 h-5" src={InProgressIcon} />
						<span className="text-[14px] font-medium text-primary-blue-dark">
							{ts(ToDoStatus.QA)}
						</span>
					</div>
					<div className="flex gap-2 items-center px-2 justify-center">
						<img alt="done" className="w-5 h-5" src={DoneIcon} />
						<span className="text-[14px] font-medium text-primary-blue-dark">
							{ts(ToDoStatus.DONE)}
						</span>
					</div>
				</div>
			</div>

			{/* Swimlane rows */}
			<div className="space-y-4">
				{tagGroups.map((group) => (
					<SwimlaneRow
						key={group.tagId ?? "untagged"}
						items={group.items}
						showSubTaskDetails={(subTaskId) => {
							handleShowSubTaskDetails(subTaskId);
						}}
						showTaskDetails={(taskId) => {
							handleShowTaskDetails(taskId);
						}}
						tagId={group.tagId}
						tagName={group.tagName}
					/>
				))}
			</div>
		</>
	);
}
